import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Shield, ShieldCheck, Calendar, Key, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Sparkles, ExternalLink, Save, Monitor, KeyRound, Trash2, Plus, MonitorSmartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
// WebAuthn client — see frontend/src/lib/webauthn.js for the single swap
// point between local stub and the real @simplewebauthn/browser library.
import { startRegistration, isPasskeyClientAvailable } from '../lib/webauthn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  it_admin: 'IT Admin',
  hr_admin: 'HR Admin',
  viewer: 'Viewer'
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Phase B: trusted-device count + revoke-all UI. Count is loaded on
  // mount and refreshed after any revoke action. `null` while loading
  // so we can show a spinner instead of "0 trusted devices" (which
  // would flash as a stale state before the first fetch lands).
  const [trustedCount, setTrustedCount] = useState(null);
  const [revokingTrusted, setRevokingTrusted] = useState(false);
  const loadTrustedCount = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/trusted-devices/count`);
      setTrustedCount(r.data?.count ?? 0);
    } catch (_) {
      // Silently — if the endpoint fails we don't want to break the whole
      // profile page. Card will render "unknown"; user can retry.
      setTrustedCount(null);
    }
  }, []);
  useEffect(() => { loadTrustedCount(); }, [loadTrustedCount]);

  // Phase C: Passkey management. Fetch list on mount, refresh after any
  // add / delete. `null` while loading so the UI shows a spinner rather
  // than an empty state.
  const [passkeys, setPasskeys] = useState(null);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const [passkeyNickname, setPasskeyNickname] = useState('');
  const [deletingPasskey, setDeletingPasskey] = useState(null); // credential_id
  const loadPasskeys = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/passkeys`);
      setPasskeys(r.data?.passkeys || []);
    } catch (_) {
      setPasskeys([]);
    }
  }, []);
  useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

  const handleRegisterPasskey = async () => {
    // Bail before hitting the server when the WebAuthn client isn't
    // installed — the register/begin call would come back 200 but the
    // browser ceremony can't run, wasting a challenge doc + confusing
    // the user. Toast-with-hint is a much cleaner failure mode.
    if (!isPasskeyClientAvailable()) {
      toast.error('Passkeys not available', {
        description: 'The @simplewebauthn/browser package is not installed in this build. Ask an admin to add it and redeploy.',
      });
      return;
    }
    setRegisteringPasskey(true);
    try {
      // Server generates a challenge + registration options.
      const beginRes = await axios.post(`${BACKEND_URL}/api/auth/passkey/register/begin`, {
        nickname: passkeyNickname.trim() || null,
      });
      const { options, challenge_id } = beginRes.data;
      // Browser triggers the WebAuthn ceremony — user proves themselves
      // via Touch ID / Windows Hello / hardware key, generates a keypair.
      const credential = await startRegistration({ optionsJSON: options });
      // Server verifies the attestation and stores the public key.
      await axios.post(
        `${BACKEND_URL}/api/auth/passkey/register/finish?challenge_id=${encodeURIComponent(challenge_id)}`,
        {
          credential,
          nickname: passkeyNickname.trim() || null,
        }
      );
      toast.success('Passkey registered', {
        description: 'You can now sign in with just a touch on this device.',
      });
      setPasskeyNickname('');
      await loadPasskeys();
    } catch (err) {
      // User dismissed the OS prompt — quiet no-op.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return;
      // "InvalidStateError" is what the browser throws when the same
      // authenticator has been registered before — surface a helpful hint.
      if (err?.name === 'InvalidStateError') {
        toast.error('This device is already registered', {
          description: 'You can rename or remove existing passkeys below.',
        });
        return;
      }
      toast.error('Passkey registration failed', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setRegisteringPasskey(false);
    }
  };

  const handleDeletePasskey = async (credentialId, nickname) => {
    if (!window.confirm(
      `Remove ${nickname || 'this passkey'}?\n\n`
      + 'You won\'t be able to sign in with it any more. If it\'s your only auth method other than password, make sure you know your password.'
    )) return;
    setDeletingPasskey(credentialId);
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/passkeys/${encodeURIComponent(credentialId)}`);
      toast.success('Passkey removed');
      await loadPasskeys();
    } catch (err) {
      toast.error('Failed to remove passkey', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setDeletingPasskey(null);
    }
  };

  // Follow-up 4: Active sessions self-service. List all live sessions
  // for the current user with device/IP metadata; revoke individual ones
  // to sign out other devices. `null` while loading so the UI can show
  // a spinner without flashing "no sessions" prematurely.
  const [sessions, setSessions] = useState(null);
  const [revokingSession, setRevokingSession] = useState(null);
  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/sessions`);
      setSessions(r.data?.sessions || []);
    } catch (_) {
      setSessions([]);
    }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleRevokeSession = async (sessionId, deviceLabel) => {
    if (!window.confirm(
      `Sign out of ${deviceLabel}?\n\n`
      + 'That browser will be kicked to the login page on its next '
      + 'action. Your current session stays active.'
    )) return;
    setRevokingSession(sessionId);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/sessions/${encodeURIComponent(sessionId)}/revoke`);
      toast.success('Session revoked');
      await loadSessions();
    } catch (err) {
      toast.error('Failed to revoke session', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setRevokingSession(null);
    }
  };

  // Ultra-simple UA parse — extracts a human label from the raw string.
  // We stored the UA at login time; showing the whole thing would be
  // noisy (150+ chars), so we grab the browser + OS families.
  const shortUA = (ua) => {
    if (!ua) return 'Unknown browser';
    let os = 'Unknown OS';
    if (/Windows/i.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
    else if (/iPhone|iPad/i.test(ua)) os = 'iOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/Linux/i.test(ua)) os = 'Linux';
    let browser = 'Browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/Chrome\//.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
    return `${browser} on ${os}`;
  };

  const handleRevokeAllTrusted = async () => {
    // Confirm before nuking — the very next login on all those devices
    // (including this one) will prompt for 2FA again.
    if (!window.confirm(
      'Sign out of all trusted browsers?\n\n'
      + 'Every trusted device (including this one) will need to re-verify '
      + '2FA the next time it signs in. Your current session stays active '
      + 'until you log out.'
    )) return;
    setRevokingTrusted(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/auth/trusted-devices/revoke-all`);
      const n = r.data?.revoked_count ?? 0;
      toast.success(`Revoked ${n} trusted device${n === 1 ? '' : 's'}`, {
        description: 'Every browser will prompt for 2FA on the next sign-in.',
      });
      await loadTrustedCount();
    } catch (err) {
      toast.error('Failed to revoke trusted devices', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setRevokingTrusted(false);
    }
  };

  // Universal Key state
  const [keyMasked, setKeyMasked] = useState('');
  const [keySet, setKeySet] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keyLoading, setKeyLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch Universal Key status on mount (super admin only)
  React.useEffect(() => {
    if (isSuperAdmin) {
      const token = sessionStorage.getItem('harmony_token');
      axios.get(`${BACKEND_URL}/api/settings/universal-key`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setKeyMasked(res.data.key_masked);
        setKeySet(res.data.key_set);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const handleUpdateKey = async () => {
    if (!newKey.trim()) {
      toast.error('Please enter a valid key');
      return;
    }
    setKeyLoading(true);
    try {
      const token = sessionStorage.getItem('harmony_token');
      await axios.put(`${BACKEND_URL}/api/settings/universal-key`, 
        { key: newKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Universal Key updated successfully');
      setEditingKey(false);
      setNewKey('');
      // Refresh masked key
      const res = await axios.get(`${BACKEND_URL}/api/settings/universal-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeyMasked(res.data.key_masked);
      setKeySet(res.data.key_set);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update key');
    } finally {
      setKeyLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    
    setLoading(false);
    
    if (result.success) {
      setSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } else {
      setError(result.error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Profile</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account settings
          </p>
        </div>

        {/* Temporary Password Warning */}
        {user?.is_temporary_password && (
          <Alert className="border-warning bg-warning/10">
            <AlertCircle className="h-4 w-4 text-warning" />
            <AlertDescription>
              You are using a temporary password. Please change it for security.
            </AlertDescription>
          </Alert>
        )}

        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Account Information
            </CardTitle>
            <CardDescription>
              Your account details and role
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm">Username</Label>
                <p className="font-medium">{user?.username}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm">Full Name</Label>
                <p className="font-medium">{user?.full_name || 'Not set'}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </Label>
                <p className="font-medium">{user?.email || 'Not set'}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Role
                </Label>
                <Badge className="mt-1">
                  {ROLE_LABELS[user?.role] || user?.role}
                </Badge>
              </div>
              
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Member Since
                </Label>
                <p className="font-medium">{formatDate(user?.created_at)}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm">Status</Label>
                <Badge variant="outline" className={user?.is_active ? 'text-green-600 border-green-200' : 'text-red-600 border-red-200'}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication Status */}
        <Card data-testid="profile-2fa-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>
              Your account is protected with TOTP-based two-factor authentication
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">2FA is enabled</p>
                <p className="text-xs text-green-600">
                  Your account requires a verification code from your authenticator app on every login.
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Contact your administrator if you need to reset your 2FA device.
            </p>
          </CardContent>
        </Card>

        {/* Active sessions — self-service session listing + revoke */}
        <Card data-testid="profile-sessions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MonitorSmartphone className="w-5 h-5" />
              Active sessions
            </CardTitle>
            <CardDescription>
              Every browser you&apos;re currently signed in on. Revoke any you don&apos;t recognise — they&apos;ll be kicked to the login page on their next action.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active sessions found.</p>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                      s.is_current ? 'bg-emerald-50 border-emerald-200' : 'bg-card'
                    }`}
                    data-testid={`session-row-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{shortUA(s.user_agent)}</p>
                        {s.is_current && (
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                            This device
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.ip ? `IP ${s.ip} · ` : ''}
                        {s.last_used_at ? `Last active ${new Date(s.last_used_at).toLocaleString()}` : 'Just created'}
                      </p>
                    </div>
                    {!s.is_current && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeSession(s.id, shortUA(s.user_agent))}
                        disabled={revokingSession === s.id}
                        data-testid={`revoke-session-${s.id}`}
                      >
                        {revokingSession === s.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <X className="w-4 h-4 text-destructive" />}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              To sign out of every other device at once, use &quot;Sign out all other devices&quot; below in Trusted Devices.
              Sessions expire automatically after 30 days of inactivity.
            </p>
          </CardContent>
        </Card>

        {/* Trusted Devices — 2FA skip cookies */}
        <Card data-testid="profile-trusted-devices-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Trusted devices
            </CardTitle>
            <CardDescription>
              Browsers where you ticked &quot;Trust this browser for 30 days&quot; after signing in.
              Each trusted device skips the 2FA prompt on future sign-ins for 30 days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/40 border">
              <div>
                <p className="text-sm font-medium">
                  {trustedCount === null
                    ? 'Loading…'
                    : trustedCount === 0
                      ? 'No trusted devices'
                      : `${trustedCount} trusted device${trustedCount === 1 ? '' : 's'}`}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {trustedCount > 0
                    ? 'Revoke if you signed in on a public or shared browser and forgot to uncheck the box.'
                    : 'Trust cookies are set after you verify 2FA and tick the box.'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevokeAllTrusted}
                disabled={revokingTrusted || !trustedCount}
                data-testid="revoke-trusted-devices-btn"
              >
                {revokingTrusted ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Revoke all
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Changing your password or having an admin reset it also revokes every trusted device automatically.
            </p>
          </CardContent>
        </Card>

        {/* Passkeys — WebAuthn one-touch sign-in */}
        <Card data-testid="profile-passkeys-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Passkeys
            </CardTitle>
            <CardDescription>
              Sign in with a fingerprint, Face ID, Windows Hello, or hardware key — no password or 2FA prompt.
              Each passkey is bound to a specific device. Add one per device you want to sign in from.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Register form */}
            <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
              <Label htmlFor="passkey-nickname" className="text-sm">
                Add a passkey on this device
              </Label>
              <div className="flex gap-2">
                <Input
                  id="passkey-nickname"
                  placeholder="Nickname (optional) — e.g. MacBook Touch ID"
                  value={passkeyNickname}
                  onChange={(e) => setPasskeyNickname(e.target.value)}
                  disabled={registeringPasskey}
                  data-testid="passkey-nickname-input"
                />
                <Button
                  onClick={handleRegisterPasskey}
                  disabled={registeringPasskey}
                  data-testid="register-passkey-btn"
                >
                  {registeringPasskey ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Your browser will prompt for a biometric or security key. The private half stays on this device — the server only sees the public key.
              </p>
            </div>

            {/* Existing passkeys list */}
            {passkeys === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : passkeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No passkeys registered yet. Add one above to sign in with just a touch.
              </p>
            ) : (
              <div className="space-y-2">
                {passkeys.map((pk) => (
                  <div
                    key={pk.credential_id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card"
                    data-testid={`passkey-row-${pk.credential_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {pk.nickname || 'Passkey'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added {pk.created_at ? new Date(pk.created_at).toLocaleDateString() : '—'}
                        {pk.last_used_at ? ` · last used ${new Date(pk.last_used_at).toLocaleDateString()}` : ' · never used'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePasskey(pk.credential_id, pk.nickname)}
                      disabled={deletingPasskey === pk.credential_id}
                      data-testid={`delete-passkey-${pk.credential_id}`}
                    >
                      {deletingPasskey === pk.credential_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4 text-destructive" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              You can keep password + 2FA as a fallback — deleting all passkeys just means you sign in the old way.
            </p>
          </CardContent>
        </Card>

        {/* Universal Key Management (Super Admin only) */}
        {isSuperAdmin && (
        <Card data-testid="universal-key-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Universal Key (AI Services)
            </CardTitle>
            <CardDescription>
              Manage your Emergent Universal Key for AI-powered features like resume parsing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current key status */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Current Key</p>
                <p className="text-xs font-mono text-muted-foreground">
                  {keySet ? keyMasked : 'No key configured'}
                </p>
              </div>
              <Badge variant={keySet ? 'default' : 'destructive'} className="shrink-0">
                {keySet ? 'Active' : 'Not Set'}
              </Badge>
            </div>

            {/* Budget notice */}
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm space-y-1.5">
              <p className="font-medium text-amber-800">Running low on AI credits?</p>
              <p className="text-amber-700 text-xs">
                Add balance to your Universal Key on the Emergent platform:
                <strong> Profile &rarr; Universal Key &rarr; Add Balance</strong>.
                You can also enable auto top-up so you never run out.
              </p>
              <a 
                href="https://www.emergentagent.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline mt-1"
              >
                Go to Emergent Platform <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Update key */}
            {editingKey ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="new-key">New Universal Key</Label>
                  <div className="relative">
                    <Input
                      id="new-key"
                      type={showKey ? 'text' : 'password'}
                      placeholder="sk-emergent-..."
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      className="pr-10 font-mono text-sm"
                      data-testid="universal-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpdateKey} 
                    disabled={keyLoading || !newKey.trim()}
                    size="sm"
                    data-testid="save-key-btn"
                  >
                    {keyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Key
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setEditingKey(false); setNewKey(''); setShowKey(false); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                variant="outline" 
                onClick={() => setEditingKey(true)}
                data-testid="update-key-btn"
              >
                <Key className="mr-2 h-4 w-4" />
                {keySet ? 'Update Key' : 'Add Key'}
              </Button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-600">
                    Password changed successfully
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    className="pr-10"
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter new password"
                    className="pr-10"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    className="pr-10"
                    data-testid="confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <Button type="submit" disabled={loading} data-testid="change-password-submit">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ProfilePage;
